// src/pages/Learn.js
// Learn landing page — seeker-facing educational content.
// Route: /learn (authenticated, seeker nav)

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { LEARN_TOPICS } from '../data/learnContent';
import SmartSearch from '../components/shared/SmartSearch';
import { trackEvent } from '../utils/analytics';

const LearnTopicCard = ({ topic }) => {
  const navigate = useNavigate();

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/learn/${topic.slug}`)}
      onKeyDown={(e) => e.key === 'Enter' && navigate(`/learn/${topic.slug}`)}
      style={{
        display: 'block', textAlign: 'left', padding: 24,
        background: '#FFFFFF', borderRadius: 12, border: '1px solid #E5E7EB',
        cursor: 'pointer', userSelect: 'none', transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#bb9749'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontSize: 28, marginBottom: 12 }}>{topic.icon}</div>
      <h3 style={{
        fontSize: 17, fontWeight: 600, color: '#111827', marginBottom: 8,
        fontFamily: 'Montserrat, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {topic.title}
        <ChevronRight size={18} color="#D1D5DB" style={{ transition: 'color 0.15s' }} />
      </h3>
      <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5, fontFamily: 'Montserrat, sans-serif', marginBottom: 12 }}>
        {topic.subtitle}
      </p>
      <span style={{ fontSize: 12, color: '#9CA3AF', fontFamily: 'Montserrat, sans-serif' }}>
        {topic.articleCount} {topic.articleCount === 1 ? 'article' : 'articles'}
      </span>
    </span>
  );
};

const Learn = () => {
  const navigate = useNavigate();

  // Track page view
  React.useEffect(() => { trackEvent('learn_page_view', {}); }, []);

  // Flatten all Learn articles into SmartSearch-compatible format
  const searchArticles = useMemo(() => {
    const articles = [];
    for (const topic of LEARN_TOPICS) {
      for (const section of topic.sections) {
        articles.push({
          id: section.id,
          title: section.title,
          content: section.content,
          keywords: section.keywords,
          searchMetadata: section.searchMetadata,
          categoryName: topic.title,
          categoryColor: '#FFF7ED',
          categoryTextColor: '#bb9749',
          audienceLabel: 'Learn',
          _topicSlug: topic.slug,
          _sectionSlug: section.slug,
        });
      }
    }
    return articles;
  }, []);

  const handleSearchResultClick = (article) => {
    trackEvent('learn_search', { query: 'result_click', article: article._sectionSlug });
    navigate(`/learn/${article._topicSlug}/${article._sectionSlug}`);
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', background: '#F9FAFB', fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 20px 80px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
            Explore the Journey
          </h1>
          <p style={{ fontSize: 15, color: '#6B7280', maxWidth: 480, margin: '0 auto 28px' }}>
            Your guide to spiritual &amp; wellness practices — whether you're brand new or going deeper.
          </p>

          {/* SmartSearch */}
          <SmartSearch
            articles={searchArticles}
            onResultClick={handleSearchResultClick}
            placeholder="Search articles..."
          />
        </div>

        {/* Topics Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {LEARN_TOPICS.map((topic) => (
            <LearnTopicCard key={topic.id} topic={topic} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Learn;
